/**
 * Plantilla y validación de "Importar Pólizas" (Pólizas → ... → Importar Pólizas).
 *
 * Un renglón por MOVIMIENTO. La columna "Póliza" agrupa los renglones que forman una misma
 * póliza; los datos de la póliza (tipo, fecha, concepto) se llenan en cualquiera de sus
 * renglones y los demás pueden ir vacíos.
 *
 * Una póliza se acepta o se rechaza COMPLETA: si uno de sus renglones tiene error, o la
 * póliza no cuadra, todos sus renglones salen en errores.xlsx. Mandar sólo los renglones
 * buenos dejaría una póliza descuadrada.
 *
 * A propósito sin React ni antd: así el workbook y la validación se pueden generar y revisar
 * con un `node script.js`, sin levantar la app. Mismo criterio que
 * `Components/importarAsignaciones.utils.js`, de donde se reusan los helpers.
 */

import * as XLSX from "xlsx";
import moment from "moment";
import {
  texto,
  normalizarTexto,
  registrarLlave,
  buscarEnIndices,
  parsearFechaExcel,
  hojaDeFilas,
  hojaDeCatalogo,
  hojaDeInstrucciones,
} from "../../../../Components/importarAsignaciones.utils";
import { headerStyle } from "../../../../Components/ExcelEstilos";

export const NOMBRE_PLANTILLA = "plantillaImportarPolizas.xlsx";

// Mismos topes que el backend (Controllers/importarPolizas.controllers.js).
export const MAX_POLIZAS = 2000;
export const MAX_MOVIMIENTOS = 20000;

export const COL = {
  poliza: "Póliza",
  tipo: "Tipo de Póliza",
  fecha: "Fecha",
  concepto: "Concepto Póliza",
  proyecto: "Proyecto",
  cuenta: "Cuenta Contable",
  cargo: "Cargo",
  abono: "Abono",
  conceptoMovimiento: "Concepto Movimiento",
};

export const COLUMNAS = [
  COL.poliza,
  COL.tipo,
  COL.fecha,
  COL.concepto,
  COL.proyecto,
  COL.cuenta,
  COL.cargo,
  COL.abono,
  COL.conceptoMovimiento,
];

const ANCHOS = [10, 18, 12, 36, 24, 18, 14, 14, 36];

// tipoDePolizaId (1..6) es la clase contable del tipo; el nombre lo pone cada empresa.
const CLASE_TIPO_POLIZA = {
  1: "Ingreso",
  2: "Egreso",
  3: "Traslado",
  4: "Nómina",
  5: "Pago",
  6: "Diario",
};

const round2 = (valor) => Math.round((Number(valor) + Number.EPSILON) * 100) / 100;

const formatoImporte = (valor) =>
  round2(valor).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Cuentas contables ───────────────────────────────────────────────────────

// Los niveles con valor, en orden, hasta el primero vacío (mismo criterio que
// `nivelesDeCuenta` del backend). Los niveles vacíos pueden venir como null.
const nivelesDeCuenta = (cuenta) => {
  const niveles = [cuenta.nivel1, cuenta.nivel2, cuenta.nivel3, cuenta.nivel4, cuenta.nivel5].map(
    (nivel) => (nivel === null || nivel === undefined ? "" : String(nivel).trim()),
  );
  const conValor = [];
  for (const nivel of niveles) {
    if (!nivel) break;
    conValor.push(nivel);
  }
  return conValor;
};

// "102-01-001": el mismo formato del Excel de Póliza Manual.
export const codigoCuenta = (cuenta) => nivelesDeCuenta(cuenta).join("-");

// Lo que escribe el usuario, con cualquier separador ("102-01-001", "102.01.001",
// "102 01 001"), queda como "102-01-001".
const llaveCuentaConSeparador = (valor) =>
  texto(valor)
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean)
    .join("-");

// Sin separadores ("10201001"). Puede quedarle a dos cuentas con niveles de ancho
// distinto; el índice lo marca ambiguo.
const llaveCuentaPegada = (valor) => texto(valor).toUpperCase().replace(/[^A-Z0-9]/g, "");

// ─── Catálogos ───────────────────────────────────────────────────────────────

/**
 * Recibe la respuesta de `polizas/datosParaImportarPolizas` y arma los índices de cruce.
 * Una llave que le queda a dos registros se marca como ambigua: el código de cuenta y los
 * nombres NO son únicos en el sistema.
 */
export const prepararCatalogos = (resData) => {
  const tiposDePoliza = resData?.tiposDePoliza || [];
  const cuentasContables = resData?.cuentasContables || [];
  const proyectos = resData?.proyectos || [];

  const tiposPorNombre = new Map();
  tiposDePoliza.forEach((tipo) => registrarLlave(tiposPorNombre, normalizarTexto(tipo.nombre), tipo));

  const cuentasPorCodigo = new Map();
  const cuentasPorPegado = new Map();
  cuentasContables.forEach((cuenta) => {
    const codigo = codigoCuenta(cuenta);
    if (!codigo) return;
    registrarLlave(cuentasPorCodigo, codigo.toUpperCase(), cuenta);
    registrarLlave(cuentasPorPegado, llaveCuentaPegada(codigo), cuenta);
  });

  const proyectosPorNombre = new Map();
  proyectos.forEach((proyecto) => registrarLlave(proyectosPorNombre, normalizarTexto(proyecto.nombre), proyecto));

  return {
    tiposDePoliza,
    cuentasContables,
    proyectos,
    indices: { tiposPorNombre, cuentasPorCodigo, cuentasPorPegado, proyectosPorNombre },
  };
};

const resolverTipo = (indices, valor) => {
  const { encontrado, ambiguo } = buscarEnIndices([indices.tiposPorNombre], [normalizarTexto(valor)]);
  if (ambiguo) return { error: `"${texto(valor)}" le queda a más de un tipo de póliza` };
  if (!encontrado) return { error: `"${texto(valor)}" no existe (revisa la hoja "Tipos de Póliza")` };
  return { tipo: encontrado };
};

const resolverProyecto = (indices, valor) => {
  const { encontrado, ambiguo } = buscarEnIndices([indices.proyectosPorNombre], [normalizarTexto(valor)]);
  if (ambiguo) return { error: `"${texto(valor)}" le queda a más de un proyecto` };
  if (!encontrado) {
    return { error: `"${texto(valor)}" no existe o no tienes acceso (revisa la hoja "Proyectos")` };
  }
  return { proyecto: encontrado };
};

const resolverCuenta = (indices, valor) => {
  const escrito = texto(valor);
  const conSeparador = /[^A-Za-z0-9]/.test(escrito);
  const { encontrado, ambiguo } = conSeparador
    ? buscarEnIndices([indices.cuentasPorCodigo], [llaveCuentaConSeparador(escrito)])
    : buscarEnIndices([indices.cuentasPorCodigo, indices.cuentasPorPegado], [escrito.toUpperCase(), llaveCuentaPegada(escrito)]);
  if (ambiguo) {
    return {
      error: conSeparador
        ? `"${escrito}" le queda a más de una cuenta contable con el mismo código`
        : `"${escrito}" le queda a más de una cuenta; escríbela con guiones (ej. "102-01-001")`,
    };
  }
  if (!encontrado) {
    return {
      error: `"${escrito}" no existe o no es de último nivel (revisa la hoja "Cuentas Contables")`,
    };
  }
  return { cuenta: encontrado };
};

// ─── Importes y fechas ───────────────────────────────────────────────────────

// Devuelve { valor } (0 cuando la celda viene vacía o en cero) o { error }. Acepta "$" y
// comas de miles, que es como suelen venir los importes pegados de otro reporte.
const parsearImporte = (valor) => {
  if (valor === undefined || valor === null || valor === "") return { valor: 0 };
  if (typeof valor === "number") {
    if (!Number.isFinite(valor)) return { error: "no es un número" };
    if (valor < 0) return { error: "no puede ser negativo" };
    return { valor: round2(valor) };
  }
  const limpio = texto(valor).replace(/[$,\s]/g, "");
  if (limpio === "") return { valor: 0 };
  if (!/^-?\d+(\.\d+)?$/.test(limpio)) return { error: `"${texto(valor)}" no es un número` };
  const numero = Number(limpio);
  if (numero < 0) return { error: "no puede ser negativo" };
  return { valor: round2(numero) };
};

// "YYYY-MM-DD" o null. parsearFechaExcel no revisa que el día exista (31/02 pasa).
const parsearFecha = (valor) => {
  const fecha = parsearFechaExcel(valor);
  if (!fecha) return null;
  const m = moment(fecha, "YYYY-MM-DD", true);
  if (!m.isValid() || m.year() < 1900 || m.year() > 2100) return null;
  return fecha;
};

// ─── Lectura del archivo ─────────────────────────────────────────────────────

// Los encabezados del archivo se cruzan sin acentos ni mayúsculas, por si alguien los
// reescribió a mano ("Poliza", "CARGO"). Se juntan de TODAS las filas: una fila no trae la
// llave de una celda vacía.
const mapearEncabezados = (filas) => {
  const reales = new Map();
  filas.forEach((fila) => {
    Object.keys(fila).forEach((llave) => {
      if (llave.startsWith("__")) return;
      const normal = normalizarTexto(llave);
      if (!reales.has(normal)) reales.set(normal, llave);
    });
  });
  const mapa = {};
  COLUMNAS.forEach((columna) => {
    mapa[columna] = reales.get(normalizarTexto(columna)) || columna;
  });
  return mapa;
};

const vacio = (valor) => valor === undefined || valor === null || texto(valor) === "";

const cuadrarPorProyecto = (renglones) => {
  const grupos = new Map();
  renglones.forEach((renglon) => {
    const llave = String(renglon.proyecto._id);
    const grupo = grupos.get(llave) || { nombre: renglon.proyecto.nombre, cargos: 0, abonos: 0 };
    grupo.cargos = round2(grupo.cargos + renglon.cargo);
    grupo.abonos = round2(grupo.abonos + renglon.abono);
    grupos.set(llave, grupo);
  });
  return [...grupos.values()].map((grupo) => ({ ...grupo, diferencia: round2(grupo.cargos - grupo.abonos) }));
};

const mensajeDescuadre = (grupos) => {
  const descuadrados = grupos.filter((grupo) => grupo.diferencia !== 0);
  if (descuadrados.length === 0) return null;
  if (grupos.length === 1) {
    const [grupo] = grupos;
    return `La póliza no cuadra: cargos ${formatoImporte(grupo.cargos)}, abonos ${formatoImporte(grupo.abonos)} (diferencia ${formatoImporte(grupo.diferencia)})`;
  }
  const detalle = descuadrados
    .slice(0, 3)
    .map((grupo) => `${grupo.nombre} (cargos ${formatoImporte(grupo.cargos)}, abonos ${formatoImporte(grupo.abonos)})`)
    .join(" | ");
  const restantes = descuadrados.length - 3;
  return `Cada proyecto de la póliza debe cuadrar por separado. Descuadrados: ${detalle}${restantes > 0 ? ` y ${restantes} más` : ""}`;
};

/**
 * Un campo de la póliza (tipo, fecha, concepto) se toma del primer renglón que lo trae. Un
 * renglón posterior que trae OTRO valor es error: casi siempre es que se repitió la clave de
 * la póliza para dos pólizas distintas.
 */
const campoDePoliza = (renglones, nombreCampo, comparable) => {
  let definido = null;
  renglones.forEach((renglon) => {
    const crudo = renglon.crudo[nombreCampo];
    if (vacio(crudo)) return;
    if (!definido) {
      definido = { crudo, comparable: comparable(crudo), numeroFila: renglon.numeroFila };
      return;
    }
    if (comparable(crudo) !== definido.comparable) {
      renglon.errores.push({
        campo: COL[nombreCampo],
        motivo: `"${texto(crudo)}" no coincide con el de la fila ${definido.numeroFila} de la misma póliza`,
      });
    }
  });
  return definido;
};

/**
 * Pasada previa sobre todo el archivo. La llama `prepararContextoArchivo` del importador
 * genérico; `validarFila` sólo lee lo que dejó aquí.
 *
 * @param {Array}  filas      las filas de la primera hoja (sheet_to_row_object_array)
 * @param {object} catalogos  lo que devolvió prepararCatalogos
 * @returns {{ porFila: Map<number, object>, resumen: object }}
 *   `porFila` va por la posición de la fila en `filas` (el `indice` que recibe validarFila).
 */
export const analizarArchivo = (filas, catalogos) => {
  const indices = catalogos?.indices;
  const porFila = new Map();
  const resumen = { polizas: 0, polizasConError: 0, movimientos: 0, cargos: 0 };

  if (!indices) {
    filas.forEach((fila, i) =>
      porFila.set(i, {
        ok: false,
        errores: [{ campo: "—", motivo: "No se cargaron los catálogos; recarga la pantalla" }],
      }),
    );
    return { porFila, resumen };
  }

  const columnas = mapearEncabezados(filas);
  const leer = (fila, columna) => fila[columnas[columna]];

  // 1. Cada renglón por su cuenta: lo que se puede resolver sin ver a sus hermanos.
  const renglones = filas.map((fila, i) => {
    const renglon = {
      indice: i,
      numeroFila: (fila.__rowNum__ ?? i) + 1,
      clave: texto(leer(fila, COL.poliza)),
      crudo: {
        tipo: leer(fila, COL.tipo),
        fecha: leer(fila, COL.fecha),
        concepto: leer(fila, COL.concepto),
      },
      proyectoTexto: texto(leer(fila, COL.proyecto)),
      conceptoMovimiento: texto(leer(fila, COL.conceptoMovimiento)),
      errores: [],
    };

    if (!renglon.clave) {
      renglon.errores.push({
        campo: COL.poliza,
        motivo: "está vacía (los renglones con el mismo valor forman una póliza)",
      });
    }

    const cuentaTexto = texto(leer(fila, COL.cuenta));
    if (!cuentaTexto) {
      renglon.errores.push({ campo: COL.cuenta, motivo: "está vacía" });
    } else {
      const { cuenta, error } = resolverCuenta(indices, cuentaTexto);
      if (error) renglon.errores.push({ campo: COL.cuenta, motivo: error });
      renglon.cuenta = cuenta;
    }

    if (renglon.proyectoTexto) {
      const { proyecto, error } = resolverProyecto(indices, renglon.proyectoTexto);
      if (error) renglon.errores.push({ campo: COL.proyecto, motivo: error });
      renglon.proyectoPropio = proyecto;
    }

    const cargo = parsearImporte(leer(fila, COL.cargo));
    const abono = parsearImporte(leer(fila, COL.abono));
    if (cargo.error) renglon.errores.push({ campo: COL.cargo, motivo: cargo.error });
    if (abono.error) renglon.errores.push({ campo: COL.abono, motivo: abono.error });
    if (!cargo.error && !abono.error) {
      if (cargo.valor > 0 && abono.valor > 0) {
        renglon.errores.push({ campo: `${COL.cargo}/${COL.abono}`, motivo: "trae los dos; un movimiento es cargo O abono" });
      } else if (cargo.valor === 0 && abono.valor === 0) {
        renglon.errores.push({ campo: `${COL.cargo}/${COL.abono}`, motivo: "están vacíos; pon el importe en uno de los dos" });
      }
    }
    renglon.cargo = cargo.valor || 0;
    renglon.abono = abono.valor || 0;

    return renglon;
  });

  // 2. Por póliza.
  const grupos = new Map();
  renglones.forEach((renglon) => {
    if (!renglon.clave) return;
    if (!grupos.has(renglon.clave)) grupos.set(renglon.clave, []);
    grupos.get(renglon.clave).push(renglon);
  });

  const datosDePoliza = new Map();

  grupos.forEach((grupo, clave) => {
    const primero = grupo[0];
    const erroresPoliza = [];

    const tipoDef = campoDePoliza(grupo, "tipo", (valor) => normalizarTexto(valor));
    const fechaDef = campoDePoliza(grupo, "fecha", (valor) => parsearFecha(valor) || `invalida:${texto(valor)}`);
    const conceptoDef = campoDePoliza(grupo, "concepto", (valor) => texto(valor));

    let tipo = null;
    if (!tipoDef) {
      erroresPoliza.push({ campo: COL.tipo, motivo: "falta (llénalo en un renglón de la póliza)" });
    } else {
      const resuelto = resolverTipo(indices, tipoDef.crudo);
      if (resuelto.error) erroresPoliza.push({ campo: COL.tipo, motivo: `${resuelto.error} (fila ${tipoDef.numeroFila})` });
      tipo = resuelto.tipo || null;
    }

    let fecha = null;
    if (!fechaDef) {
      erroresPoliza.push({ campo: COL.fecha, motivo: "falta (llénala en un renglón de la póliza)" });
    } else {
      fecha = parsearFecha(fechaDef.crudo);
      if (!fecha) {
        erroresPoliza.push({
          campo: COL.fecha,
          motivo: `"${texto(fechaDef.crudo)}" no es una fecha válida; usa DD/MM/AAAA (fila ${fechaDef.numeroFila})`,
        });
      }
    }

    const concepto = conceptoDef ? texto(conceptoDef.crudo) : "";
    if (!concepto) erroresPoliza.push({ campo: COL.concepto, motivo: "falta (llénalo en un renglón de la póliza)" });

    // El proyecto de la póliza es el primero que aparezca; un renglón sin proyecto lo hereda.
    const conProyecto = grupo.find((renglon) => renglon.proyectoTexto);
    const proyectoPoliza = conProyecto ? conProyecto.proyectoPropio : null;
    if (!conProyecto) {
      erroresPoliza.push({ campo: COL.proyecto, motivo: "falta (llénalo al menos en un renglón de la póliza)" });
    }

    if (grupo.length < 2) {
      erroresPoliza.push({ campo: COL.poliza, motivo: "la póliza necesita al menos dos movimientos (un cargo y un abono)" });
    }

    grupo.forEach((renglon) => {
      renglon.proyecto = renglon.proyectoPropio || proyectoPoliza;
    });

    const conErrorPropio = grupo.filter((renglon) => renglon.errores.length > 0);

    // El cuadre sólo tiene sentido cuando todos los renglones se pudieron leer.
    if (conErrorPropio.length === 0 && erroresPoliza.length === 0) {
      const descuadre = mensajeDescuadre(cuadrarPorProyecto(grupo));
      if (descuadre) erroresPoliza.push({ campo: COL.poliza, motivo: descuadre });
    }

    if (erroresPoliza.length > 0) {
      // Los errores de la póliza van en su primer renglón (el mensaje trae la fila de origen
      // cuando aplica). Los demás renglones quedan rechazados por arrastre, abajo.
      primero.errores.push(...erroresPoliza);
    }

    const filasConError = grupo.filter((renglon) => renglon.errores.length > 0);
    if (filasConError.length > 0) {
      const numeros = filasConError.map((renglon) => renglon.numeroFila);
      grupo.forEach((renglon) => {
        if (renglon.errores.length > 0) return;
        renglon.errores.push({
          campo: COL.poliza,
          motivo: `la póliza "${clave}" tiene errores en ${numeros.length === 1 ? "la fila" : "las filas"} ${numeros.slice(0, 5).join(", ")}${numeros.length > 5 ? "…" : ""}; se rechaza completa`,
        });
      });
      resumen.polizasConError += 1;
      return;
    }

    datosDePoliza.set(clave, { tipo, fecha, concepto, proyectoPoliza });
    resumen.polizas += 1;
    resumen.movimientos += grupo.length;
    resumen.cargos = round2(resumen.cargos + grupo.reduce((acc, renglon) => acc + renglon.cargo, 0));
  });

  // 3. El veredicto de cada fila, en la forma que espera validarFila.
  renglones.forEach((renglon) => {
    if (renglon.errores.length > 0) {
      porFila.set(renglon.indice, { ok: false, errores: renglon.errores });
      return;
    }
    const poliza = datosDePoliza.get(renglon.clave);
    porFila.set(renglon.indice, {
      ok: true,
      datos: {
        poliza: renglon.clave,
        fila: renglon.numeroFila,
        tipoDePolizaId: String(poliza.tipo._id),
        tipo: poliza.tipo.nombre,
        fecha: poliza.fecha,
        concepto: poliza.concepto,
        proyectoPolizaId: String(poliza.proyectoPoliza._id),
        proyectoPoliza: poliza.proyectoPoliza.nombre,
        proyectoId: String(renglon.proyecto._id),
        proyecto: renglon.proyecto.nombre,
        cuentaContableId: String(renglon.cuenta._id),
        cuenta: codigoCuenta(renglon.cuenta),
        cuentaDescripcion: renglon.cuenta.descripcion || "",
        cargo: renglon.cargo || null,
        abono: renglon.abono || null,
        conceptoMovimiento: renglon.conceptoMovimiento || poliza.concepto,
      },
    });
  });

  return { porFila, resumen };
};

/**
 * Junta los renglones válidos (el `data` que manda el importador genérico) en las pólizas que
 * recibe `polizas/iniciarImportacionPolizas`, en el orden del archivo.
 */
export const agruparParaEnvio = (data) => {
  const polizas = new Map();
  (data || []).forEach((renglon) => {
    if (!polizas.has(renglon.poliza)) {
      polizas.set(renglon.poliza, {
        clave: renglon.poliza,
        filas: [],
        tipoDePolizaId: renglon.tipoDePolizaId,
        tipoNombre: renglon.tipo,
        fecha: renglon.fecha,
        concepto: renglon.concepto,
        proyectoId: renglon.proyectoPolizaId,
        proyectoNombre: renglon.proyectoPoliza,
        cargosAbonos: [],
      });
    }
    const poliza = polizas.get(renglon.poliza);
    poliza.filas.push(renglon.fila);
    poliza.cargosAbonos.push({
      cuentaContableId: renglon.cuentaContableId,
      cuenta: renglon.cuenta,
      proyectoId: renglon.proyectoId,
      proyecto: renglon.proyecto,
      ...(renglon.cargo ? { cargo: renglon.cargo } : { abono: renglon.abono }),
      concepto: renglon.conceptoMovimiento,
    });
  });
  return [...polizas.values()];
};

// ─── Plantilla ───────────────────────────────────────────────────────────────

const estilizarEncabezados = (hoja, columnas) => {
  columnas.forEach((columna, i) => {
    const celda = hoja[XLSX.utils.encode_cell({ r: 0, c: i })];
    if (celda) celda.s = headerStyle;
  });
  return hoja;
};

const INSTRUCCIONES = [
  "IMPORTAR PÓLIZAS",
  "",
  "Llena la hoja \"Pólizas\" (debe quedar como la PRIMERA hoja del archivo). Va un renglón por MOVIMIENTO (cargo o abono), no por póliza.",
  "",
  "Póliza: un número o texto que agrupa los movimientos. Todos los renglones con el mismo valor forman UNA póliza. Sólo sirve para agrupar: el número real lo asigna el sistema.",
  "Tipo de Póliza, Fecha y Concepto Póliza: basta con llenarlos en un renglón de la póliza; los demás pueden ir vacíos. Si los repites, deben ser iguales.",
  "Tipo de Póliza: el nombre tal como aparece en la hoja \"Tipos de Póliza\".",
  "Fecha: DD/MM/AAAA (ej. 01/10/2026).",
  "Proyecto: el nombre tal como aparece en la hoja \"Proyectos\". El de la póliza es el primero que escribas; un renglón sin proyecto usa ése.",
  "  Si pones proyectos distintos en una misma póliza, CADA proyecto debe cuadrar por separado (igual que en Póliza Manual con \"Varios proyectos\").",
  "Cuenta Contable: el código con guiones, tal como aparece en la hoja \"Cuentas Contables\" (ej. 102-01-001). Sólo cuentas de último nivel.",
  "Cargo / Abono: el importe va en UNA de las dos columnas, mayor que cero.",
  "Concepto Movimiento: opcional. Si lo dejas vacío se usa el concepto de la póliza.",
  "",
  "Cada póliza debe cuadrar (total de cargos = total de abonos). Si un renglón tiene un error o la póliza no cuadra, se rechaza la póliza completa y sale en \"errores.xlsx\".",
  "Si un movimiento usa la cuenta contable de un banco, se registra el movimiento en el banco igual que en Póliza Manual.",
  "",
  "IMPORTANTE: volver a subir un archivo que ya se importó DUPLICA las pólizas. Si una importación quedó incompleta, descarga \"No importadas\" en los resultados y sube sólo ese archivo.",
  "",
  "Ejemplo (dos pólizas):",
  "Póliza | Tipo de Póliza | Fecha | Concepto Póliza | Proyecto | Cuenta Contable | Cargo | Abono | Concepto Movimiento",
  "1 | Diario | 01/10/2026 | Ajuste de octubre | Matriz | 601-01-001 | 1500 |  | ",
  "1 |  |  |  |  | 102-01-001 |  | 1500 | Pago de servicio",
  "2 | Egreso | 02/10/2026 | Renta de oficina | Matriz | 603-02 | 8000 |  | ",
  "2 |  |  |  |  | 102-01-001 |  | 8000 | ",
];

/**
 * El libro que se descarga con "Descargar Plantilla". La hoja de datos va primero porque el
 * importador genérico sólo lee la primera hoja.
 */
export const construirPlantilla = (catalogos, filas = []) => {
  const libro = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    libro,
    estilizarEncabezados(hojaDeFilas(COLUMNAS, filas, ANCHOS), COLUMNAS),
    "Pólizas",
  );

  const columnasTipos = ["Tipo de Póliza", "Descripción", "Clase"];
  XLSX.utils.book_append_sheet(
    libro,
    estilizarEncabezados(
      hojaDeCatalogo(
        columnasTipos,
        (catalogos?.tiposDePoliza || []).map((tipo) => ({
          "Tipo de Póliza": tipo.nombre,
          Descripción: tipo.descripcion,
          Clase: CLASE_TIPO_POLIZA[tipo.tipoDePolizaId] || "",
        })),
        [28, 40, 12],
      ),
      columnasTipos,
    ),
    "Tipos de Póliza",
  );

  const columnasCuentas = ["Cuenta Contable", "Descripción"];
  XLSX.utils.book_append_sheet(
    libro,
    estilizarEncabezados(
      hojaDeCatalogo(
        columnasCuentas,
        (catalogos?.cuentasContables || []).map((cuenta) => ({
          "Cuenta Contable": codigoCuenta(cuenta),
          Descripción: cuenta.descripcion,
        })),
        [20, 50],
      ),
      columnasCuentas,
    ),
    "Cuentas Contables",
  );

  const columnasProyectos = ["Proyecto"];
  XLSX.utils.book_append_sheet(
    libro,
    estilizarEncabezados(
      hojaDeCatalogo(
        columnasProyectos,
        (catalogos?.proyectos || []).map((proyecto) => ({ Proyecto: proyecto.nombre })),
        [40],
      ),
      columnasProyectos,
    ),
    "Proyectos",
  );

  XLSX.utils.book_append_sheet(libro, hojaDeInstrucciones(INSTRUCCIONES), "Instrucciones");

  return libro;
};

/**
 * Renglones de la plantilla para las pólizas que NO se crearon (el `original` que guarda el
 * backend en cada resultado con error o detenida). Así se reintenta sólo lo que faltó sin
 * duplicar lo que ya entró.
 */
export const filasPlantillaDePolizas = (polizas) => {
  const filas = [];
  (polizas || []).forEach((poliza) => {
    const fecha = poliza.fecha ? moment(poliza.fecha, "YYYY-MM-DD").format("DD/MM/YYYY") : "";
    (poliza.cargosAbonos || []).forEach((valueCA, i) => {
      filas.push({
        [COL.poliza]: poliza.clave,
        [COL.tipo]: i === 0 ? poliza.tipoNombre : null,
        [COL.fecha]: i === 0 ? fecha : null,
        [COL.concepto]: i === 0 ? poliza.concepto : null,
        [COL.proyecto]: valueCA.proyecto || (i === 0 ? poliza.proyectoNombre : null),
        [COL.cuenta]: valueCA.cuenta,
        [COL.cargo]: valueCA.cargo || null,
        [COL.abono]: valueCA.abono || null,
        [COL.conceptoMovimiento]: valueCA.concepto && valueCA.concepto !== poliza.concepto ? valueCA.concepto : null,
      });
    });
  });
  return filas;
};
